/**
 * FreeOptionsList container
 *
 * @author Diogo Guedes <dguedes@ubiwhere.com>
 *
 */

import React, { useEffect, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { ThemeContext } from 'styled-components';

import { actions } from 'store/rootSlices';
import * as selectors from './logic/selectors';
import { AnimatedBackground } from '@paco_ua/pacoui';

import IsolatedOptionsTableStructure from './structure';
import { checkAreAllFiltersFilled, checkHasFilters } from './utils';

const IsolatedOptionsTable: React.FC<{
  t;
  onLoad;
  onError;
  onPeriod;
  registrationId?: number | null;
  mandatoryFilters?: boolean;
  onSelectRow?: (any) => void;
}> = ({ t, onLoad, onError, onPeriod, mandatoryFilters, registrationId, onSelectRow }) => {
  const {
    onMount,
    setSearchFilter,
    setDepartmentFilter,
    setCycleFilter,
    fetchIsolatedOptions,
    onUnmount,
  } = actions.IsolatedOptionsTable;

  const { currentLocale } = useSelector(selectors.getAppSlice);

  const dispatch = useDispatch();

  const theme = useContext(ThemeContext);

  const {
    loadingStatus,
    errorStatus,
    filterParams,
    isolatedOptionsList,
    isolatedOptionsPage,
    departmentFilterList,
    cycleFilterList,
    year,
    semester,
  } = useSelector(selectors.getIsolatedOptionsListSlice);

  const ucs = useSelector(selectors.getUcs);

  useEffect(() => {
    dispatch(onMount({ registrationId, mandatoryFilters }));
    return () => {
      dispatch(onUnmount());
    };
  }, [currentLocale]);

  useEffect(() => {
    onPeriod && onPeriod({ year, semester });
  }, [year, semester]);

  useEffect(() => {
    onLoad && onLoad(loadingStatus.loading);
  }, [loadingStatus]);

  useEffect(() => {
    onError && onError(errorStatus.errLoadingIsolatedOptions);
  }, [errorStatus]);

  return (
    <>
      {loadingStatus.loading && (
        <LoadingWrapper>
          <AnimatedBackground height={theme.sizes.large} width="20%" />
          <AnimatedBackground height={theme.sizes.large} width="50%" />
          <AnimatedBackground height={theme.sizes.large} width="80%" />
          <AnimatedBackground height={theme.sizes.large} width="80%" />
          <AnimatedBackground height={theme.sizes.large} width="80%" />
          <AnimatedBackground height={theme.sizes.large} width="80%" />
          <AnimatedBackground height={theme.sizes.large} width="50%" />
        </LoadingWrapper>
      )}
      {!loadingStatus.loading && !errorStatus.errLoadingIsolatedOptions && (
        <IsolatedOptionsTableStructure
          t={t}
          mandatoryFilters={mandatoryFilters}
          disableFilters={loadingStatus.loadingTable}
          loading={loadingStatus.loadingTable}
          fetchIsolatedOptions={(params) => {
            dispatch(fetchIsolatedOptions({ ...params, mandatoryFilters, registrationId }));
          }}
          addFilters={{
            searchPlaceholder: t('sgh.searchFreeOptionPlaceholder', { textOnly: true }),
            dropdownDepartmentPlaceholder: t('sgh.freeOptionsDropdownDepartmentPlaceholder', {
              textOnly: true,
            }),
            dropdownCoursePlaceholder: t('sgh.freeOptionsDropdownCoursePlaceholder', {
              textOnly: true,
            }),
            onSearch: (value) => {
              dispatch(setSearchFilter(value));
            },
            onCycleFilterChange: (value) => {
              dispatch(setCycleFilter(value));
            },
            onDepartmentFilterChange: (value) => {
              dispatch(setDepartmentFilter(value));
            },
            filterDepartments: departmentFilterList,
            filterCycles: cycleFilterList,
          }}
          filterParams={filterParams}
          total={isolatedOptionsPage.totalRecords}
          data={isolatedOptionsList}
          registrationId={registrationId}
          onSelectRow={onSelectRow}
          ucs={ucs}
        />
      )}
    </>
  );
};

export default IsolatedOptionsTable;

const LoadingWrapper = styled.div`
  width: 100%;
  height: 300px;

  margin-top: 20px;

  * {
    margin: 8px 0px;
  }
`;
